import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from '@/types/user.types';
import { Pencil, HardDrive, Trash2, Check } from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
} from '@tanstack/react-table';
import { useTableFilters } from '@/hooks/useTableFilters';
import { FilterDialog } from '@/components/shared/FilterDialog';
import { TableHeader } from '@/components/shared/tables/TableHeader/TableHeader';
import { TableRow } from '@/components/shared/tables/TableRow/TableRow';
import { getUserTableColumns } from '@/constants/tables/userTable.config';
import { TableButton } from '@/components/ui/TableButton';
import { PERMISSIONS } from '@/contexts/PermissionsContext';
import { useDeleteConfirmation } from '@/hooks/useDeleteConfirmation';

interface UserTableProps {
  users: User[];
  onDeactivate: (id: string) => Promise<void>;
  onReactivate: (id: string) => Promise<void>;
  onEdit: (user: User) => void;
  onManageDevices: (user: User) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  onDeactivate,
  onReactivate,
  onEdit,
  onManageDevices,
}) => {
  const { t } = useTranslation();
  const useDeactivateMode = true;
  const { confirmDelete } = useDeleteConfirmation();
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);

  const columnConfigs = useMemo(() => getUserTableColumns(t), [t]);

  const {
    filterState,
    columnFilters,
    openFilter,
    closeFilter,
    setSearch,
    toggleOption,
    clearFilter,
    applyFilter,
    getFilteredOptions,
  } = useTableFilters(users, columnConfigs);

  const columnHelper = createColumnHelper<User>();

  const columns = useMemo(
    () => [
      ...columnConfigs.map((config) => {
        const accessor =
          typeof config.accessor === 'function'
            ? config.accessor
            : // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (row: User) => (row as any)[config.accessor as keyof User];

        return columnHelper.accessor(accessor, {
          id: config.id,
          header: ({ column }) => {
            const filterValue = column.getFilterValue() as string[] | undefined;
            const isFiltered = Array.isArray(filterValue) && filterValue.length > 0;
            return (
              <TableHeader
                header={column}
                columnConfig={config}
                onFilterClick={(columnId) => openFilter(columnId, columnFilters)}
                isFiltered={isFiltered}
              />
            );
          },
          cell: config.cell,
          enableSorting: config.enableSorting,
          enableColumnFilter: config.enableFilter,
          filterFn: (row, _columnId, filterValue) => {
            if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
            const value = accessor(row.original);
            return filterValue.includes(value as string);
          },
        });
      }),
      columnHelper.display({
        id: 'actions',
        header: t('pages.users.columns.actions'),
        cell: ({ row }) => (
          <div className="flex space-x-2">
            <TableButton
              permission={PERMISSIONS.USER_UPDATE}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row.original);
              }}
              icon={<Pencil className="h-4 w-4" />}
              label={t('common.edit')}
              variant="primary"
            />

            <TableButton
              permission={PERMISSIONS.DEVICE_ASSOCIATE}
              onClick={(e) => {
                e.stopPropagation();
                onManageDevices(row.original);
              }}
              icon={<HardDrive className="h-4 w-4" />}
              label={t('pages.users.deviceAssociation')}
              variant="primary"
            />

            {row.original.metadata?.status === 'active' ? (
              <TableButton
                permission={PERMISSIONS.USER_DELETE}
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.original.id) {
                    const userName = row.original.credentials?.username || row.original.id;
                    confirmDelete(userName, 'user', useDeactivateMode, () =>
                      onDeactivate(row.original.id!)
                    );
                  }
                }}
                icon={<Trash2 className="h-4 w-4" />}
                label={t('common.deactivate')}
                variant="danger"
                fixedWidth={true}
              />
            ) : (
              <TableButton
                permission={PERMISSIONS.USER_DELETE}
                onClick={(e) => {
                  e.stopPropagation();
                  if (row.original.id) {
                    onReactivate(row.original.id);
                  }
                }}
                icon={<Check className="h-4 w-4" />}
                label={t('common.activate')}
                variant="primary"
                fixedWidth={true}
              />
            )}
          </div>
        ),
      }),
    ],
    [
      columnConfigs,
      t,
      columnFilters,
      onDeactivate,
      onReactivate,
      onEdit,
      onManageDevices,
      columnHelper,
      confirmDelete,
      openFilter,
      useDeactivateMode,
    ]
  );

  const table = useReactTable({
    data: users,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const handleRowClick = (userId: string | undefined) => {
    if (!userId) return;
    setSelectedRowId((current) => (current === userId ? null : userId));
  };

  return (
    <div className="bg-brand-white rounded-lg shadow-lg">
      {/* Filter Dialogs */}
      {columnConfigs
        .filter((config) => config.enableFilter)
        .map((config) => {
          let filterTitleKey;
          const { id } = config;

          if (id === 'personalInfo.name') {
            filterTitleKey = 'pages.users.dialog.personalInfo.nameFilterTitle';
          } else if (id === 'personalInfo.surname1') {
            filterTitleKey = 'pages.users.dialog.personalInfo.surname1FilterTitle';
          } else if (id === 'credentials.username') {
            filterTitleKey = 'pages.users.dialog.credentials.usernameFilterTitle';
          } else if (id === 'role') {
            filterTitleKey = 'pages.users.dialog.roleFilterTitle';
          } else if (id === 'timezone') {
            filterTitleKey = 'pages.users.dialog.personalInfo.timezoneFilterTitle';
          } else if (id === 'metadata.status') {
            filterTitleKey = 'pages.users.dialog.metadata.statusFilterTitle';
          } else if (id === 'metadata.subscription') {
            filterTitleKey = 'pages.users.dialog.metadata.subscriptionFilterTitle';
          } else if (id === 'metadata.subscriptionExpiryDate') {
            filterTitleKey = 'pages.users.dialog.metadata.subscriptionExpiryDateFilterTitle';
          } else {
            filterTitleKey = `pages.users.dialog.${id}FilterTitle`;
          }

          return (
            <FilterDialog
              key={config.id}
              isOpen={!!filterState[config.id]?.isOpen}
              onClose={() => closeFilter(config.id)}
              title={t(filterTitleKey)}
              searchValue={filterState[config.id]?.search || ''}
              onSearchChange={(value) => setSearch(config.id, value)}
              options={getFilteredOptions(config.id)}
              selectedOptions={filterState[config.id]?.selected || []}
              onOptionToggle={(option) => toggleOption(config.id, option)}
              onClear={() => clearFilter(config.id)}
              onApply={() => applyFilter(config.id)}
              getOptionLabel={config.getOptionLabel}
              textCase={config.textCase}
            />
          );
        })}

      {/* Table Container with scroll */}
      <div className="overflow-x-auto">
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          <table className="w-full divide-y divide-gray-200">
            <thead className="sticky top-0 z-10 bg-brand-gray-dark">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-3 py-2 text-left text-xs font-medium text-brand-white sentence-case tracking-wider"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-200">
              {table.getRowModel().rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  row={row}
                  index={index}
                  selectedRowId={selectedRowId}
                  onRowClick={handleRowClick}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
